<script setup lang="ts">
import BlockNumbers from './BlockNumbers.vue'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import HeaderNumbers from '~/components/pages/home/NewComponents/HeaderNumbers.vue'
import { getRandomInt } from '~/utils/getRandomNumbers'
import { updateBlockNumbers } from '~/utils/lotteryNumbers'
import { trackClearAll } from '~/tracking/homeEvents.js'
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'

const emit = defineEmits(['update:modelValue', 'scroll-to-actions'])

defineOptions({
	name: 'SelectNumbers',
})

interface Block {
	first: (string | null)[]
	last: (string | null)[] | any
}

const props = defineProps({
	classContainerHeader: {
		type: String,
		default: 'flex flex-col items-center gap-5 max-w-3xl mx-auto',
	},
	classContainerNumbers: {
		type: String,
		default: 'flex justify-center items-center flex-wrap w-fit mx-auto',
	},
	showHeaderNumbers: {
		type: Boolean,
		default: true,
	},
	modelValue: {
		type: Object,
		required: false,
		default: () => ({ first: [], last: [] }),
	},
	rules: {
		type: Object,
		required: true,
	},
	showAnimationFloatingBalls: {
		type: Boolean,
		default: true,
	},
	classContainerBlockNumbers: {
		type: String,
		default: 'flex flex-wrap justify-center sm:justify-start gap-x-7 sm:gap-x-2 gap-y-5 sm:gap-y-2',
	},
	showClassAnimation: {
		type: Boolean,
		default: true,
	},
	isMutipleBets: {
		type: Boolean,
		default: false,
	},
	enableAutoScrollOnFirstLimit: {
		type: Boolean,
		default: false,
	},
	preferActionsOnFirstLimit: {
		type: Boolean,
		default: false,
	},
	scrollTopOffset: {
		type: Number,
		default: 0,
	},
})

const initialBlock = {
	first: [],
	last: [],
}

const blockNumbers = ref<Block | any>({ ...initialBlock })
const visibleFirstCount = ref(0)
const visibleLastCount = ref(0)
const isAnimatingRandom = ref(false)
const firstBlockRef = ref<HTMLElement | null>(null)
const lastBlockRef = ref<HTMLElement | null>(null)
const isReadyForAutoScroll = ref(false)
const hasAutoScrolledByLimit = ref(false)
const hasAutoScrolledToActionsByLastLimit = ref(false)

// Animacion de forma manual
const manuallyRevealedIndexesFirst = ref(new Set<number>())
const manuallyRevealedIndexesLast = ref(new Set<number>())

// Arrays para controlar qué números deben animarse en BlockNumbers
const animatedFirstNumbers = ref<number[]>([])
const animatedLastNumbers = ref<number[]>([])

const selectedFirstCount = computed(() => {
	if (!Array.isArray(blockNumbers.value.first)) return 0
	return blockNumbers.value.first.filter((n: number | null) => n !== null).length
})

const selectedLastCount = computed(() => {
	if (!Array.isArray(blockNumbers.value.last)) return 0
	return blockNumbers.value.last.filter((n: number | null) => n !== null).length
})

const firstTotalCount = computed(() => props.rules?.first_block?.total || 0)
const lastTotalCount = computed(() => props.rules?.last_block?.total || 0)

const scrollToElement = (element: HTMLElement | null) => {
	if (!import.meta.client || !element) return
	const offset = Number(props.scrollTopOffset) || 0
	const safeGap = 12
	const top = element.getBoundingClientRect().top + window.scrollY - offset - safeGap
	window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

const handleFirstBlockLimitReached = () => {
	if (props.preferActionsOnFirstLimit || !props.rules?.last_block) {
		emit('scroll-to-actions')
		return
	}

	if (selectedLastCount.value >= lastTotalCount.value) {
		emit('scroll-to-actions')
		return
	}

	scrollToElement(lastBlockRef.value)
}

const setNumbers = async () => {
	blockNumbers.value.first.forEach((_: any, i: number) => {
		onManualBallRevealed({ index: i, isYellow: false })
	})

	if (blockNumbers.value.last === null) return
	blockNumbers.value.last.forEach((_: any, i: number) => {
		onManualBallRevealed({ index: i, isYellow: true })
	})
}

// Inicializar con todas las bolitas visibles pero vacías
const initializeVisibleCounts = () => {
	visibleFirstCount.value = blockNumbers.value.first.length
	visibleLastCount.value = blockNumbers.value.last?.length || 0
	setNumbers()
	resetManualAnimation()
}

const clearNumber = (content: any) => {
	const { property, index } = content as { property: string; index: number }
	if (blockNumbers.value[property][index] === null) return
	blockNumbers.value[property][index] = null

	onManualBallRevealed({ index, isYellow: property === 'last', action: 'remove' })
}

const changeCode = () => {
	emit('update:modelValue', { ...blockNumbers.value })
}

const revealNumbersSequentially = async () => {
	const { first, last } = blockNumbers.value
	const firstBlockLength = first.length || 0
	const lastBlockLength = last?.length || 0

	isAnimatingRandom.value = true

	visibleFirstCount.value = 0
	visibleLastCount.value = 0

	animatedFirstNumbers.value = []
	animatedLastNumbers.value = []

	const firstSelectedNumbers = first.filter((n: number) => n !== null)
	const lastSelectedNumbers = last ? last.filter((n: number) => n !== null) : []

	for (let i = 0; i < Math.max(firstBlockLength, firstSelectedNumbers.length); i++) {
		await new Promise((resolve) => setTimeout(resolve, 400))

		if (i < firstBlockLength) {
			visibleFirstCount.value = i + 1
		}

		if (i < firstSelectedNumbers.length) {
			animatedFirstNumbers.value.push(firstSelectedNumbers[i])
		}
	}

	if (lastBlockLength > 0 || lastSelectedNumbers.length > 0) {
		for (let i = 0; i < Math.max(lastBlockLength, lastSelectedNumbers.length); i++) {
			await new Promise((resolve) => setTimeout(resolve, 400))

			if (i < lastBlockLength) {
				visibleLastCount.value = i + 1
			}

			if (i < lastSelectedNumbers.length) {
				animatedLastNumbers.value.push(lastSelectedNumbers[i])
			}
		}
	}

	await new Promise((resolve) => setTimeout(resolve, 300))

	isAnimatingRandom.value = false
}

const setRandomRepeatNumbers = async () => {
	const { min, max } = props.rules.first_block

	blockNumbers.value.first.forEach((_: any, i: number) => {
		blockNumbers.value.first[i] = getRandomInt(min, max)
		onManualBallRevealed({ index: i, isYellow: false })
	})
	changeCode()

	if (blockNumbers.value.last === null) {
		return await revealNumbersSequentially()
	}
}

const calcSelectSize = (blockKey: any) => {
	const rule = blockKey === 'first' ? props.rules.first_block : props.rules.last_block
	if (!rule) return 0

	if (props.isMutipleBets) {
		// múltiple: first => min_select + 1, last => min_select
		return blockKey === 'first' ? (rule.min_select ?? 0) + 1 : (rule.min_select ?? 0)
	}
	const isKeno = props.rules?.id === ID_KENO_FR ? rule.min_select : rule.total
	// regular: use total
	return isKeno ?? 0
}

const ensureSize = (arr: number[], size: number) => {
	if (!Array.isArray(arr)) return Array(size).fill(null)
	if (arr.length === size) return arr
	if (arr.length > size) return arr.slice(0, size)
	return arr.concat(Array(size - arr.length).fill(null))
}

const shuffleInPlace = (a: number[]) => {
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		;[a[i], a[j]] = [a[j], a[i]]
	}
	return a
}

const getUniqueRandoms = ({ min = 0, max = 0, count = 0, exclude = new Set() }) => {
	const candidates = []
	for (let n = min; n <= max; n++) {
		if (!exclude.has(n)) candidates.push(n)
	}
	shuffleInPlace(candidates)
	const picked = candidates.slice(0, count)
	// rellena con null si no alcanza
	if (picked.length < count) {
		picked.push(...Array(count - picked.length).fill(null))
	}
	return picked
}

const setRandomNotRepeatNumbers = async () => {
	// --- FIRST BLOCK ---
	const firstSize = calcSelectSize('first')
	const { min: firstMin, max: firstMax } = props.rules.first_block

	blockNumbers.value.first = ensureSize(blockNumbers.value.first, firstSize)

	const pickedFirst = getUniqueRandoms({
		min: firstMin,
		max: firstMax,
		count: firstSize,
		exclude: new Set(), // no excluimos nada para el first
	})

	// Escribimos resultados en el array existente para conservar reactividad
	for (let i = 0; i < firstSize; i++) {
		blockNumbers.value.first[i] = pickedFirst[i]
		onManualBallRevealed({ index: i, isYellow: false })
	}
	changeCode()

	// --- LAST BLOCK (si existe) ---
	if (blockNumbers.value.last == null) {
		return await revealNumbersSequentially()
	}

	const lastSize = calcSelectSize('last')
	const { min: lastMin, max: lastMax } = props.rules.last_block

	blockNumbers.value.last = ensureSize(blockNumbers.value.last, lastSize)

	// Excluir lo ya usado en first (ignora nulls)
	const excludeAll = new Set(blockNumbers.value.first.filter((n: number) => n != null))

	const pickedLast = getUniqueRandoms({
		min: lastMin,
		max: lastMax,
		count: lastSize,
		exclude: excludeAll,
	})

	for (let i = 0; i < lastSize; i++) {
		blockNumbers.value.last[i] = pickedLast[i]
		onManualBallRevealed({ index: i, isYellow: true })
	}
	changeCode()

	return await revealNumbersSequentially()
}

const setRandom = async () => {
	resetManualAnimation()
	if (IDS_LOTTERIES_CLASSIC.includes(props.rules.id)) {
		await setRandomRepeatNumbers()
		return
	}
	await setRandomNotRepeatNumbers()
}

const shouldShowNumber = (index: number, type: 'first' | 'last') => {
	if (type === 'first') {
		return manuallyRevealedIndexesFirst.value.has(index)
	} else {
		return manuallyRevealedIndexesLast.value.has(index)
	}
}

const onManualBallRevealed = ({ index, isYellow, action }: { index: number; isYellow: boolean; action?: string }) => {
	const set = isYellow ? manuallyRevealedIndexesLast.value : manuallyRevealedIndexesFirst.value
	if (action === 'remove') {
		set.delete(index)
		return
	}
	set.add(index)
	if (props.rules?.id !== ID_KENO_FR) return
	const { total } = props.rules.first_block
	if (visibleFirstCount.value < total) {
		visibleFirstCount.value++
	}
}

const resetManualAnimation = () => {
	manuallyRevealedIndexesFirst.value.clear()
	manuallyRevealedIndexesLast.value.clear()
}

const updateBlock = (property: string, ruleProperty: string) => {
	updateBlockNumbers({
		property: property,
		blockNumbers: blockNumbers.value,
		rules: props.rules,
		ruleProperty: ruleProperty,
		mode: null,
		multipleBets: props.isMutipleBets,
	})
}

const clearAll = (page = 'home') => {
	updateBlock('first', 'first_block')
	updateBlock('last', 'last_block')
	resetManualAnimation()
	trackClearAll({ context: `Clear all in page ${page}` })
}

watch(
	() => props.modelValue,
	(newVal) => {
		if (JSON.stringify(blockNumbers.value) !== JSON.stringify(newVal)) {
			blockNumbers.value = newVal ? { ...newVal } : { ...initialBlock }
			if (!isAnimatingRandom.value) {
				initializeVisibleCounts()
			}
		}
	},
	{ immediate: true, deep: true },
)

watch(
	() => blockNumbers.value,
	(newVal) => {
		if (JSON.stringify(props.modelValue) !== JSON.stringify(newVal)) {
			emit('update:modelValue', { ...newVal })
		}
	},
	{ deep: true },
)

watch(selectedFirstCount, (newCount, oldCount) => {
	const firstTotal = firstTotalCount.value
	if (!firstTotal || isReadyForAutoScroll.value === false || props.enableAutoScrollOnFirstLimit === false) return

	if (newCount < firstTotal) {
		hasAutoScrolledByLimit.value = false
		return
	}

	const reachedLimitNow = oldCount < firstTotal && newCount >= firstTotal
	if (!reachedLimitNow || hasAutoScrolledByLimit.value || isAnimatingRandom.value) return

	hasAutoScrolledByLimit.value = true
	nextTick(() => {
		handleFirstBlockLimitReached()
	})
})

watch(selectedLastCount, (newCount, oldCount) => {
	const lastTotal = lastTotalCount.value
	if (!lastTotal || isReadyForAutoScroll.value === false || props.enableAutoScrollOnFirstLimit === false) return

	if (newCount < lastTotal) {
		hasAutoScrolledToActionsByLastLimit.value = false
		return
	}

	const reachedLimitNow = oldCount < lastTotal && newCount >= lastTotal
	if (!reachedLimitNow || hasAutoScrolledToActionsByLastLimit.value || isAnimatingRandom.value) return

	hasAutoScrolledToActionsByLastLimit.value = true
	nextTick(() => {
		if (selectedFirstCount.value < firstTotalCount.value) {
			scrollToElement(firstBlockRef.value)
			return
		}
		emit('scroll-to-actions')
	})
})

onMounted(() => {
	nextTick(() => {
		isReadyForAutoScroll.value = true
	})
})

defineExpose({
	setRandom,
	resetManualAnimation,
	clearAll,
	clearNumber,
})
</script>

<template>
	<section class="space-y-14 max-w-4xl">
		<!-- Header Numbers Display - Siempre visible -->
		<HeaderNumbers
			v-if="showHeaderNumbers"
			:class="props.classContainerHeader"
			:class-container-numbers="props.classContainerNumbers"
			:show-class-animation="props.showClassAnimation"
			:block-numbers="blockNumbers"
			:rules="props.rules"
			:visible-first-count="visibleFirstCount"
			:visible-last-count="visibleLastCount"
			:should-show-number="IDS_LOTTERIES_CLASSIC.includes(props.rules.id) ? () => true : shouldShowNumber"
			@clear-number="clearNumber"
			@clear-all="clearAll"
		/>
		<slot name="extra-section" />

		<!-- Bloques de Selección -->
		<div
			v-if="props.rules.first_block"
			ref="firstBlockRef"
		>
			<BlockNumbers
				v-model="blockNumbers.first"
				block="firstBalls"
				:rule="props.rules.first_block"
				:is-repeated-numbers="IDS_LOTTERIES_CLASSIC.includes(props.rules.id)"
				:is-animating-random="isAnimatingRandom"
				:animated-numbers="animatedFirstNumbers"
				:show-animation-floating-balls="
					IDS_LOTTERIES_CLASSIC.includes(props.rules.id) ? false : props.showAnimationFloatingBalls
				"
				:class-container-numbers="props.classContainerBlockNumbers"
				:id-lottery="props.rules.id"
				:is-mutiple-bets="props.isMutipleBets"
				@manual-ball-revealed="onManualBallRevealed"
			/>
		</div>
		<div
			v-if="props.rules.last_block"
			ref="lastBlockRef"
		>
			<BlockNumbers
				v-model="blockNumbers.last"
				block="lastBalls"
				:rule="props.rules.last_block"
				:is-regular-ball="false"
				:is-animating-random="isAnimatingRandom"
				:animated-numbers="animatedLastNumbers"
				:show-animation-floating-balls="props.showAnimationFloatingBalls"
				:class-container-numbers="props.classContainerBlockNumbers"
				:id-lottery="props.rules.id"
				:is-mutiple-bets="props.isMutipleBets"
				@manual-ball-revealed="onManualBallRevealed"
			/>
		</div>
	</section>
</template>

<style scoped>
.bounce-enter-active {
	animation: bounce-in 0.6s;
}

.bounce-leave-active {
	animation: bounce-in 0.6s reverse;
}

@keyframes bounce-in {
	0% {
		transform: scale(0) rotateY(180deg);
		opacity: 0;
	}
	50% {
		transform: scale(1.1) rotateY(90deg);
		opacity: 0.8;
	}
	100% {
		transform: scale(1) rotateY(0deg);
		opacity: 1;
	}
}
</style>
